import React, { memo, useCallback, useMemo, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
const SimpleReactSelect = React.lazy(() => import('@formElements/SimpleReactSelect/indexCopy'));
import cn from 'classnames';

import { TouchableButton, TouchableDiv } from '../../../common/Touchable';
import styles from './hamburgerMenu.module.scss';
import { ReactComponent as Hamburger } from '@assets/images/hamburger.svg';
import { MENU_BUTTONS } from '../constants';

import { setErrorPopUp } from '@reducers/notification';
import { setOfflineVoid } from '@reducers/orders';

import OfflinePayScreen from '@components/common/OptionsMenus/OfflinePayScreen';
import OfflinePayScreenLandscape from '@components/common/OptionsMenus/OfflinePayScreenLandscape';
import AboutModal from './AboutModal';
import ToggleButton from '@components/common/ToggleButton/ToggleButton';
import { setMyChecks } from '@reducers/storeChecks';
import { LABEL } from '@veriables';

import { useHamburgerActionHandler, INITIAL_INPUTS } from './hambugeractionhandler';

const HamburgerMenu = ({
  serverAppTheme,
  orderingPortraitOrientation,
  isOpen,
  handleToggleHamburgerMenu,
  thirdPartyAppName,
  displayName,
  jobName,
  allChecksVisible,
  getAllChecks,
  storeId,
  removeSeat,
  setVisible,
  showOpenChecks,
  onOpenTab,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.oauth.authInfo);
  const deviceInfo = useSelector(state => state.oauth.deviceInformation);
  const userInfo = useSelector(state => state.oauth.loggedInUserData);
  const employeeJobHashTable = useSelector(state => state.oauth.employeeJobHashTable);
  const isMyCheck = useSelector(state => state.storeChecks.isOn);

  const handleToggle = useCallback(() => {
    dispatch(setMyChecks());
  }, [dispatch]);

  const {
    isOfflineScreen,
    setIsOfflineScreen,
    isAboutShow,
    setAboutTipShow,
    aboutData,
    inputs,
    setInputs,
    clickHanlder,
    handleOfflinePayment,
    isChangeJob,
    jobErrors,
    setJobErrors,
    jobDefaultValue,
    setJobDefaultValue,
    optionForDropdown,
    onJobDropdownChange,
  } = useHamburgerActionHandler({
    allChecksVisible,
    getAllChecks,
    storeId,
    removeSeat,
    setVisible,
    showOpenChecks,
    handleToggleHamburgerMenu,
    thirdPartyAppName,
    onOpenTab,
  });

  const isGiftCardDisabled = useMemo(() => {
    return auth?.loyaltyInfo?.giftCardMethods == null || auth?.loyaltyInfo?.giftCardMethods.includes('');
  }, [auth]);

  const serverAppNavigationMenu = useMemo(() => deviceInfo?.deviceGroup?.serverAppNavigationMenu || {}, [deviceInfo]);
  const emptyDeviceGroup = useMemo(() => !deviceInfo?.deviceGroup, [deviceInfo]);

  const buttonVisibility = useMemo(
    () => ({
      allChecks: emptyDeviceGroup || (serverAppNavigationMenu?.allChecks && allChecksVisible),
      openChecks: !allChecksVisible,
      loyaltyAccountBalance: auth?.loyaltyInfo?.supportsLoyalty && (emptyDeviceGroup || serverAppNavigationMenu?.loyaltyBalance),
      giftBalance: (auth?.loyaltyInfo?.supportsGiftCardPayment || auth?.loyaltyInfo?.supportsGiftCardSale) && (emptyDeviceGroup || serverAppNavigationMenu?.giftCardBalance),
      gameCardBalance: auth?.gamingSystemConfig !== null && (emptyDeviceGroup || serverAppNavigationMenu?.gameCardBalance),
      lauchExternalApp: !!(thirdPartyAppName && auth?.thirdPartyAppUrl),
      offlinePayment: emptyDeviceGroup || serverAppNavigationMenu?.offlinePayment,
      tipOnly: emptyDeviceGroup || serverAppNavigationMenu?.tipOnly,
      cashOut: (emptyDeviceGroup || serverAppNavigationMenu?.cashOut) && auth?.nonIntegratedPos,
      changeJobType: emptyDeviceGroup || serverAppNavigationMenu?.changeJobType,
      about: true,
    }),
    [auth, deviceInfo, thirdPartyAppName, emptyDeviceGroup, serverAppNavigationMenu, allChecksVisible]
  );

  const resolvedButtons = useMemo(
    () => MENU_BUTTONS.map(button => ({
      ...button,
      label: button.label || thirdPartyAppName,
    })),
    [thirdPartyAppName]
  );

  const handleCloseMenu = useCallback(e => {
    e.stopPropagation();
    handleToggleHamburgerMenu();
  }, [handleToggleHamburgerMenu]);

  return (
    <>
      <div className={cn(styles.menu, isOpen ? styles.open : '')}>
        <div className={styles.content}>
          <div className="h-15 d-flex">
            <TouchableDiv
              className="mr-2"
              onClick={handleCloseMenu}
            >
              <Hamburger
                fill={serverAppTheme?.secondary_iconcolor}
                alt="appsIcon"
              />
            </TouchableDiv>

            <div className="col-10 align-items-center px-0 mx-0 pl-2">
              <div
                className={cn(
                  'col-12 p-0 m-0 text-left',
                  orderingPortraitOrientation ? 'fs_20' : 'fs_16'
                )}
              >
                {displayName}
              </div>
              <div
                className={cn(
                  'col-12 p-0 m-0 text-left',
                  orderingPortraitOrientation ? 'fs_16' : 'fs_12'
                )}
              >
                {jobName}
              </div>
            </div>
          </div>
          <div className="h-85 d-flex flex-column overflow-auto">
            {resolvedButtons.map(
              button => {
                const isGift = button.key === 'giftBalance';
                const isDisabled = isGift && isGiftCardDisabled;
                return (
                  buttonVisibility[button.key] && (
                    <React.Fragment key={button.key}>
                      <TouchableButton
                        className={cn("d-flex fw-500 justify-content-center align-items-center border-0 rounded w-100 text-center", button.key === 'changeJobType' && isChangeJob ? 'mb-0' : 'mb-2')}
                        style={{
                          background: isDisabled
                            ? serverAppTheme?.secondaryBtnDisabled
                            : serverAppTheme?.secondaryBtn,
                          color: isDisabled
                            ? serverAppTheme?.btn_font_color_disabled
                            : serverAppTheme?.btn_font_color,
                          height: orderingPortraitOrientation ? '68px' : '44px',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          pointerEvents: isDisabled ? 'none' : 'auto',
                        }}
                        onClick={(e) => {
                          if (e) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                          clickHanlder[button.key]();
                        }}
                        disabled={isDisabled}
                      >
                        {button.key === 'loyaltyAccountBalance' ? (
                          <div className={cn("px-2", orderingPortraitOrientation ? 'fs_24' : 'fs_20')}>{button.label}</div>
                        ) : (
                          <div className={cn(orderingPortraitOrientation ? 'fs_24' : 'fs_20')}>{button.label}</div>
                        )}
                      </TouchableButton>
                      {button.key === 'changeJobType' && isChangeJob && (
                        <div className="mb-2 w-100">
                          <Suspense fallback={<div>Loading...</div>}>
                            <SimpleReactSelect
                              className="w-100"
                              onChange={(data) => {
                                setJobDefaultValue(data && data.id);
                                setJobErrors('');
                                onJobDropdownChange(data?.id);
                              }}
                              isClearable
                              options={optionForDropdown}
                              name="job"
                              defaultValue={jobDefaultValue}
                              placeholder="Select Job"
                              serverAppTheme={serverAppTheme}
                            />
                          </Suspense>
                          {jobErrors && (
                            <div className="text-center fs_10 pt-1" style={{ color: serverAppTheme?.error_text }}>
                              {jobErrors}
                            </div>
                          )}
                        </div>
                      )}

                      {(button.key === 'allChecks' || button.key === 'openChecks') &&
                        Number(employeeJobHashTable?.[userInfo?.jobId]?.checkVisibility) === 1 && (
                          <div className="d-flex align-items-center justify-content-center py-2 mb-2 w-100">
                            <div className={cn("text-center fw-600 col-5 px-0", orderingPortraitOrientation ? 'fs_20' : 'fs_16')} style={{ color: serverAppTheme?.primary_text }}>
                              {LABEL().TOGGLE_TEAM_CHECKS}
                            </div>
                            <div className="col-3 px-0">
                              <ToggleButton
                                isOn={isMyCheck}
                                handleToggle={handleToggle}
                                style={{}}
                              />
                            </div>
                            <div
                              className={cn("text-center fw-600 col-4 px-0", orderingPortraitOrientation ? 'fs_20' : 'fs_16')}
                              style={{ color: serverAppTheme?.primary_text }}>
                              {LABEL().TOGGLE_MY_CHECKS}
                            </div>
                          </div>
                        )}
                    </React.Fragment>
                  )
                );
              }
            )}
          </div>
        </div>
      </div>

      {isOfflineScreen && (
        orderingPortraitOrientation ? (
          <OfflinePayScreen
            isOfflineScreen={isOfflineScreen}
            setIsOfflineScreen={setIsOfflineScreen}
            serverAppTheme={serverAppTheme}
            setErrorPopUp={(data) => dispatch(setErrorPopUp(data))}
            handleOfflinePayment={handleOfflinePayment}
            inputs={inputs}
            setInputs={setInputs}
            setOfflineVoid={(data) => dispatch(setOfflineVoid(data))}
            INITIAL_INPUTS={INITIAL_INPUTS}
          />
        ) : (
          <OfflinePayScreenLandscape
            isOfflineScreen={isOfflineScreen}
            setIsOfflineScreen={setIsOfflineScreen}
            serverAppTheme={serverAppTheme}
            setErrorPopUp={(data) => dispatch(setErrorPopUp(data))}
            handleOfflinePayment={handleOfflinePayment}
            inputs={inputs}
            setInputs={setInputs}
            setOfflineVoid={(data) => dispatch(setOfflineVoid(data))}
            INITIAL_INPUTS={INITIAL_INPUTS}
          />
        )
      )}

      <AboutModal
        isOpen={isAboutShow}
        onClose={() => setAboutTipShow(false)}
        serverAppTheme={serverAppTheme}
        aboutData={aboutData}
        orderingPortraitOrientation={orderingPortraitOrientation}
      />
    </>
  );
};

export default memo(HamburgerMenu);
